import React, { useEffect, useState } from "react";
import {
  ShoppingBag, Activity, CheckCircle2, XCircle, Store, Clock,
  Bike, Users, DollarSign, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountry } from "@/lib/country-context";
import adminApi from "@/lib/admin-api";

export default function AdminDashboardPage() {
  const { formatPrice } = useCountry();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (!stats) return <p className="text-muted-foreground">Couldn't load stats.</p>;

  const cards = [
    { label: "Total Orders", value: stats.orders.total, icon: ShoppingBag, accent: "primary", testId: "stat-orders-total" },
    { label: "Active Orders", value: stats.orders.active, icon: Activity, accent: "blue", testId: "stat-orders-active" },
    { label: "Completed", value: stats.orders.completed, icon: CheckCircle2, accent: "green", testId: "stat-orders-completed" },
    { label: "Cancelled", value: stats.orders.cancelled, icon: XCircle, accent: "red", testId: "stat-orders-cancelled" },
    { label: "Restaurants", value: stats.restaurants.total, icon: Store, accent: "primary", testId: "stat-restaurants-total", sub: `${stats.restaurants.active} active` },
    { label: "Pending Approvals", value: stats.restaurants.pending, icon: Clock, accent: "amber", testId: "stat-restaurants-pending" },
    { label: "Total Riders", value: stats.riders.total, icon: Bike, accent: "primary", testId: "stat-riders-total", sub: `${stats.riders.available} available` },
    { label: "Customers", value: stats.customers.total, icon: Users, accent: "primary", testId: "stat-customers-total" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Real-time overview of Cravve operations.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RevenueCard label="Daily Revenue" value={stats.revenue.daily} formatPrice={formatPrice} icon={DollarSign} testId="stat-revenue-daily" />
        <RevenueCard label="Weekly Revenue" value={stats.revenue.weekly} formatPrice={formatPrice} icon={TrendingUp} testId="stat-revenue-weekly" />
        <RevenueCard label="Monthly Revenue" value={stats.revenue.monthly} formatPrice={formatPrice} icon={TrendingUp} testId="stat-revenue-monthly" />
      </div>

      <div className="bg-card border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-serif font-bold text-lg">Revenue & orders — last 30 days</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={stats.revenue_trend}>
              <defs>
                <linearGradient id="g-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(20 90% 58%)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(20 90% 58%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(28 15% 20%)" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="hsl(32 12% 65%)" />
              <YAxis fontSize={11} stroke="hsl(32 12% 65%)" />
              <Tooltip
                contentStyle={{ background: "hsl(28 18% 13%)", border: "1px solid hsl(28 15% 20%)", borderRadius: 12, fontSize: 13, color: "hsl(40 22% 96%)" }}
                formatter={(v, k) => k === "revenue" ? formatPrice(v) : v}
              />
              <Area type="monotone" dataKey="revenue" stroke="hsl(20 90% 58%)" strokeWidth={2} fill="url(#g-rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LeaderboardCard title="Top restaurants by revenue" rows={stats.top_restaurants}
          renderValue={(r) => formatPrice(r.revenue)} renderMeta={(r) => `${r.orders} orders`} />
        <LeaderboardCard title="Top riders by deliveries" rows={stats.top_riders}
          renderValue={(r) => `${r.deliveries}`} renderMeta={() => "deliveries"} />
      </div>
    </div>
  );
}

const ACCENT_CLS = {
  primary: "bg-primary/10 text-primary",
  blue: "bg-blue-500/10 text-blue-600",
  green: "bg-green-500/10 text-green-600",
  red: "bg-red-500/10 text-red-600",
  amber: "bg-amber-500/10 text-amber-600",
};

function StatCard({ label, value, sub, icon: Icon, accent = "primary", testId }) {
  return (
    <div className="bg-card border rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ACCENT_CLS[accent]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-serif text-3xl font-bold mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function RevenueCard({ label, value, formatPrice, icon: Icon, testId }) {
  return (
    <div className="bg-card border rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>
      <p className="font-serif text-3xl font-bold">{formatPrice(value || 0)}</p>
    </div>
  );
}

function LeaderboardCard({ title, rows, renderValue, renderMeta }) {
  return (
    <div className="bg-card border rounded-2xl p-5">
      <p className="font-serif font-bold text-lg mb-4">{title}</p>
      {(!rows || rows.length === 0) && <p className="text-sm text-muted-foreground">No data yet.</p>}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.id} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-muted text-xs font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{r.name}</p>
              <p className="text-xs text-muted-foreground">{renderMeta(r)}</p>
            </div>
            <p className="font-semibold text-sm">{renderValue(r)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
